<template>
  <BaseDialog
    :title="displayData.title"
    :btn1="displayData.btn.submit"
    :btn2="displayData.btn.cancel"
    :btn1Loading="loading"
    :close-on-click-modal="false"
    width="600px"
    @cancel="$emit('close')"
    @confirm="onSubmit"
    @close="$emit('close')"
  >
    <BobForm
      :formRef.sync="formRef"
      :fields="displayFields"
      :model="formData"
      label-position="top"
      :rules="formRules"
    />
  </BaseDialog>
</template>

<script>
import { computed, defineComponent, nextTick, onBeforeMount, provide } from 'vue'
import { filter, get, includes, keyBy, keys, map, pickBy } from 'lodash'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BobForm from '@/components/BobForm.vue'
import ServicePairingPicker from './ServicePairingPicker.vue'
import { CreateAppointmentServicePairing, UpdateAppointmentServicePairing } from '@/api/appointmentServicePairing'
import { appointmentServicePairingMaxItems } from '@/config/appointmentServicePairing'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import formUtils from '@/utils/form'
import {
  parseServiceKey,
  toServiceKey,
  useAppointmentServicePairingServiceOptions,
} from '@/use/useAppointmentServicePairing'
import { i18n } from '@/plugins/i18n/i18n'
import { integerRules, noEmptyArrayRules, noEmptyRules, rangeRules } from '@/validation'

export default defineComponent({
  name: 'EditAppointmentServicePairingModal',
  components: {
    BaseDialog,
    BobForm,
  },
  props: {
    action: { type: String, required: true },
    selectRow: { type: Object, default: () => ({}) },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formData, formRef, initFormData, checkForm, loading } = useBaseForm()
    const {
      serviceOptions,
      mainServicePairingIds,
      loading: optionsLoading,
      fetchServiceOptions,
    } = useAppointmentServicePairingServiceOptions()

    initFormData({
      mainServiceKeys: [],
      pairedServiceKeys: [],
      order: 100,
    })

    const displayData = computed(() => ({
      title: props.action === 'create' ? '新增推薦服務搭配' : '編輯推薦服務搭配',
      btn: {
        submit: props.action === 'create' ? i18n.t('common.button.create.text') : i18n.t('common.button.save.text'),
        cancel: i18n.t('common.button.cancel.text'),
      },
    }))

    // 已選服務的名稱：以服務選項為主，選項載入前用原本的資料
    const serviceMap = computed(() => {
      const originServices = [get(props.selectRow, 'mainService'), ...get(props.selectRow, 'pairedServices', [])]
      return {
        ...keyBy(map(filter(originServices, Boolean), (service) => ({ ...service, id: toServiceKey(service) })), 'id'),
        ...keyBy(serviceOptions.value, 'id'),
      }
    })

    // 一個服務只能建立一組搭配組合：其他組合的主要服務不能再選為主要服務（編輯時排除自己）
    const occupiedMainKeys = computed(() => keys(pickBy(
      mainServicePairingIds.value,
      (pairingId) => pairingId !== get(props.selectRow, 'id'),
    )))

    // 選完服務或關閉選擇表後重新驗證；還沒選時先不顯示錯誤，送出時才提示
    const refreshValidate = (prop) => {
      nextTick(() => {
        if (!formRef.value) return
        if (formData[prop].length) formUtils.validateField(formRef.value, prop)
        else formUtils.clearValidate(formRef.value, [prop])
      })
    }

    const displayFields = computed(() => [
      {
        prop: 'mainServiceKeys',
        label: '主要服務',
        body: {
          component: ServicePairingPicker,
          props: {
            options: serviceOptions.value,
            serviceMap: serviceMap.value,
            maxSelection: 1,
            disabledKeys: formData.pairedServiceKeys,
            occupiedKeys: occupiedMainKeys.value,
            selectorTitle: '選擇主要服務',
            buttonText: '選擇主要服務',
            loading: optionsLoading.value,
          },
          on: {
            input: () => refreshValidate('mainServiceKeys'),
            selectorClose: () => refreshValidate('mainServiceKeys'),
          },
        },
      },
      {
        prop: 'pairedServiceKeys',
        label: `搭配服務（最多 ${appointmentServicePairingMaxItems} 個，順序不影響推薦）`,
        body: {
          component: ServicePairingPicker,
          props: {
            options: serviceOptions.value,
            serviceMap: serviceMap.value,
            maxSelection: appointmentServicePairingMaxItems,
            disabledKeys: formData.mainServiceKeys,
            selectorTitle: '選擇搭配服務',
            buttonText: '選擇搭配服務',
            loading: optionsLoading.value,
          },
          on: {
            input: () => refreshValidate('pairedServiceKeys'),
            selectorClose: () => refreshValidate('pairedServiceKeys'),
          },
        },
      },
      {
        prop: 'order',
        label: '排序（只影響列表顯示順序）',
        body: {
          component: 'BaseElInput',
          props: {
            type: 'number',
            placeholder: '請輸入排序',
          },
        },
      },
    ])

    const formRules = computed(() => ({
      mainServiceKeys: [noEmptyArrayRules()],
      pairedServiceKeys: [noEmptyArrayRules()],
      order: [noEmptyRules(), integerRules(), rangeRules()],
    }))

    const onSubmit = async () => {
      if (loading.value) return
      loading.value = true
      const isValid = await checkForm(formRef.value)
      if (!isValid) {
        loading.value = false
        return
      }

      const payload = {
        shopId: shopId.value,
        id: get(props.selectRow, 'id'),
        mainService: parseServiceKey(formData.mainServiceKeys[0]),
        // 主要服務不會出現在搭配服務：兩個選擇表互相排除
        pairedServices: map(filter(formData.pairedServiceKeys, (key) => !includes(formData.mainServiceKeys, key)), parseServiceKey),
        order: Number(formData.order),
      }
      const apiMethod = props.action === 'create' ? CreateAppointmentServicePairing : UpdateAppointmentServicePairing
      const [, err] = await apiMethod(payload)
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }

      window.$message.success(i18n.t(props.action === 'create' ? 'common.toast.createSuccess.text' : 'common.toast.updateSuccess.text'))
      emit('refresh')
      emit('close')
    }

    onBeforeMount(() => {
      if (props.action === 'update') {
        formData.mainServiceKeys = props.selectRow.mainService ? [toServiceKey(props.selectRow.mainService)] : []
        formData.pairedServiceKeys = map(props.selectRow.pairedServices, toServiceKey)
        formData.order = props.selectRow.order
      }
      fetchServiceOptions()
    })

    provide('formData', formData)

    return {
      formRef,
      formData,
      loading,
      displayData,
      displayFields,
      formRules,
      onSubmit,
    }
  },
})
</script>
